'use client';

import { getLocalTimeZone } from '@internationalized/date';
import { differenceInCalendarDays } from 'date-fns';
import { useState } from 'react';
import { useInteractiveLoader } from '~/components/PartnerLoader/PartnerLoader';
import { useToast } from '~/hooks/useToasts';
import { useUpdateTrip } from '~/hooks/useUpdateTrip';
import { useTripBookingStore } from '~/stores/tpv2/tripBookingStore';
import { type CalendarRange, getSafeRange, getTripDurationDaysFromStayNights } from '~/utils/dateHelpers/calendarRange';
import { useTripOverviewSummary } from './useTripOverviewSummary';
export const useTripDateSelection = () => {
  const {
    tripId,
    firstItem,
    lastItem,
    nights,
    isDateUnset,
    datesConfirmed
  } = useTripOverviewSummary();
  const toast = useToast();
  const updateTrip = useUpdateTrip();
  const {
    startLoader,
    completeProgressBar
  } = useInteractiveLoader();
  const markDatesConfirmed = useTripBookingStore(s => s.markDatesConfirmed);
  const [localRange, setLocalRange] = useState<CalendarRange | null>(null);
  const [clearCount, setClearCount] = useState(0);
  const bookingStartDate = firstItem?.startDate ?? '';
  const bookingEndDate = lastItem?.endDate ?? '';
  const serverRange = !isDateUnset && bookingStartDate && bookingEndDate ? getSafeRange(bookingStartDate, bookingEndDate) : null;
  const fallbackDurationDays = getTripDurationDaysFromStayNights(nights);
  const unsetDuration = isDateUnset ? fallbackDurationDays : null;
  const originalDuration = serverRange ? serverRange.end.compare(serverRange.start) + 1 : unsetDuration;
  const range = localRange ?? serverRange ?? getSafeRange('', '', fallbackDurationDays);
  const hasSelectedRange = localRange !== null || serverRange !== null;
  const commitRange = async (target: CalendarRange): Promise<boolean> => {
    const timeZone = getLocalTimeZone();
    startLoader();
    try {
      const result = await updateTrip({
        startDate: target.start.toDate(timeZone),
        endDate: target.end.toDate(timeZone),
        duration: differenceInCalendarDays(target.end.toDate(timeZone), target.start.toDate(timeZone)) + 1
      });
      if (result.error) {
        toast?.add({
          title: result.error,
          variant: 'error',
          position: 'middle'
        }, {
          timeout: 5000
        });
        return false;
      }
      return true;
    } finally {
      completeProgressBar();
    }
  };
  const confirmDates = async (): Promise<boolean> => {
    if (!localRange) {
      if (!serverRange) return false;
      markDatesConfirmed();
      return true;
    }
    const ok = await commitRange(localRange);
    if (ok) {
      setLocalRange(null);
      markDatesConfirmed();
    }
    return ok;
  };
  const clearDates = (): void => {
    setLocalRange(null);
    setClearCount(count => count + 1);
  };
  return {
    localRange,
    setLocalRange,
    range,
    calendarKey: `${tripId}-${clearCount}`,
    hasSelectedRange,
    originalDuration,
    datesConfirmed,
    confirmDates,
    clearDates
  };
};